import axios, { AxiosRequestConfig } from 'axios'
import * as Sentry from "@sentry/nextjs";
import client from "../apollo-client";
import {
  RetrieveIncomeVerification,
  RetrieveTemplate,
  SetDirectDepositAccount,
  SetIncomeLogin,
  SubmitPartialLead,
  UploadIdVerificationImage,
  GetCreditConsentStatus,
  CreditConsent,
} from './query/lead';
import { GetWidgetDetails } from './query/widget';
import type { IFormattedDocument } from '@/store/document';

export type WidgetField = {
  product?: string;
  integration?: string;
  channel?: string;
  topic?: string;
  key?: string;
  value?: string;
};

export const getField = (fields: WidgetField[], key: string, defaultValue?: string): string => {
  const value = fields.find((f) => f?.key === key)?.value || defaultValue || "";
  return value;
}

export const getWidgetDetails = async (widgetId: string) => {
  try {
    const { data } = await client.query({
      query: GetWidgetDetails,
      variables: { widgetId },
    });

    const language = getField(data.getWidgetDetails.fields, "language");

    return {
      companyId: data.getWidgetDetails.company.id,
      companyName: data.getWidgetDetails.company.name,
      widgetId: data.getWidgetDetails.company.widgetId,
      isArchived: data.getWidgetDetails.company.archived,
      activeProducts: data.getWidgetDetails.company.products,
      productIntegrations: data.getWidgetDetails.productIntegrations,
      personaTemplateId: data.getWidgetDetails.company.personaTemplateId,
      language,
    };
  } catch (error: any) {
    console.error(`Unable to get widget details (widgetId: ${widgetId}):`, error);
    console.error(JSON.stringify(error));
    Sentry.captureException(error, {
      level: "error",
      extra: { widgetId },
    });
    return { error: error.message };
  }
};

/* CREDIT */

/** Grabs credit consent status of a lead */
export const getCreditConsentStatus = async (token: string) => {
  try {
    const { data } = await client.query({
      query: GetCreditConsentStatus,
      variables: { accessToken: token },
    });
    return {
      leadId: data.creditConsentStatus.leadId,
      firstName: data.creditConsentStatus.firstName,
      lastName: data.creditConsentStatus.lastName,
      companyName: data.creditConsentStatus.companyDetails.name,
      consentAlreadyGiven: data.creditConsentStatus.consentAlreadyGiven,
    };
  } catch (error: any) {
    console.error(`Unable to get credit consent status with token, ${token}:`, error);
    console.error(JSON.stringify(error));
    Sentry.captureException(error, {
      level: "error",
      extra: { accessToken: token },
    });
    return { error: error.message };
  }
};

/** Returns credit bureau reports/results based on the lead and consent */
export const creditBureauResults = async (token: string, dealerCreditConsent: boolean) => {
  try {
    const { data } = await client.mutate({
      mutation: CreditConsent,
      variables: {
        dealerCreditConsent,
        accessToken: token,
      },
    });

    return {
      report: data.creditConsent.report,
      ...data.creditConsent,
    };
  } catch (error: any) {
    console.error(`Unable to retrieve credit bureaus with token, ${token}:`, error);
    console.error(JSON.stringify(error));
    Sentry.captureException(error, {
      level: "error",
      extra: {
        dealerCreditConsent,
        accessToken: token,
      },
    });
    return { error: error.message };
  }
};

/* AVA ID */

/** Submit/Create Partial Lead */
export const createPartialLead = async (widgetId: string) => {
  try {
    const { data } = await client.mutate({
      mutation: SubmitPartialLead,
      variables: {
        widgetId,
        input: {
          firstName: "AVA ID",
          lastName: "In-Store",
          emailAddress: "avaid@autocorp.ai",
        },
        product: "ID_TOOL",
      },
    });

    return {
      success: data.submitLead.success,
      leadId: data.submitLead.leadId,
    };
  } catch (error: any) {
    console.error(`Unable to create partial lead (widgetId: ${widgetId}):`, error);
    console.error(JSON.stringify(error));
    Sentry.captureException(error, {
      level: "error",
      extra: { widgetId },
    });
    return { error: error.message };
  }
};

/** Get Template Type based on leadID */
export const getTemplate = async (leadId: string) => {
  try {
    const { data } = await client.query({
      query: RetrieveTemplate,
      variables: { leadId },
    });
    return data.idVerificationTemplate;
  } catch (error: any) {
    console.error(`Unable to get template on leadId (${leadId}):`, error);
    console.error(JSON.stringify(error));
    Sentry.captureException(error, {
      level: "error",
      extra: { leadId },
    });
    return { error: error.message };
  }
};

export interface UploadProps {
  leadId: string;
  image: string;
  imageFormat: string;
  imageType: string;
};

/** Upload Id Verification Image */
export const uploadIdVerificationImage = async ({
  leadId,
  image,
  imageFormat,
  imageType,
}: UploadProps) => {
  try {
    const { data } = await client.mutate({
      mutation: UploadIdVerificationImage,
      variables: { input: { leadId, image: { fullImage: image, imageFormat }, imageType } },
    });
    return data.uploadIdVerificationImage;
  } catch (error: any) {
    console.error(`Unable to get template on leadId (${leadId}):`, error);
    console.error(JSON.stringify(error));
    Sentry.captureException(error, {
      level: "error",
      extra: { leadId },
    });
    return { error: error.message };
  }
};

export const uploadPhoto = async (leadId: string, images: IFormattedDocument[]) => {
  try {
    const data: UploadProps = {
      leadId,
      image: images[0]?.data!,
      imageType: images[0]?.image_subject!,
      imageFormat: images[0]?.format.replace("image/", "")!,
    }
    const response = await fetch("/api/id/upload", {
      method: 'POST',
      headers: { 'Content-Type': 'image/*' },
      body: JSON.stringify(data),
    }).then(r => r.json());

    return response;
  } catch (error: any) {
    console.error(`Unable to upload file to s3:`, JSON.stringify(error));
    Sentry.captureException(error, {
      level: "error",
      extra: { leadId, images },
    });
    return { error: error.message };
  }

};

/** Convert Blob data to base-64 encoded data */
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, _) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.readAsDataURL(blob);
  });
}

const testPhotoOptions = (shapes: any, imageData: string, nextStep: string) => {
  const [format, data] = imageData.replace("data:", "").split(";base64,");
  const imageSubject = nextStep.includes("selfie") // what is returned will be either "upload_selfie_basic" or "upload_selfie_liveness"
    ? "selfie_front"
    : nextStep.replace("upload_", "");

  const options: AxiosRequestConfig = {
    method: 'POST',
    url: "/api/id/test/",
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      // Authorization: `Bearer ${clientToken}`
    },
    data: {
      shapes,
      images: [
        {
          data,
          format,
          image_subject: imageSubject,
        }
      ]
    }
  }

  return options;
}

export const testPhoto = async (data: any, image: string, nextStep: string = "upload_document_front") => {
  try {
    const result = await axios(testPhotoOptions(data, image, nextStep))
    return result;
  } catch (error: any) {
    return error?.response?.data || error;
  }
}

/* AVA INCOME */

export const isIncomeVerified = async (leadId: string) => {
  try {
    const { data } = await client.query({
      query: RetrieveIncomeVerification,
      variables: { leadId },
    });
    return {
      success: data.retrieveIncomeVerification.success,
      incomeVerifiedAt: data.retrieveIncomeVerification.incomeVerifiedAt,
    };
  } catch (error: any) {
    console.error(`Unable to retrieve income verification on leadId (${leadId}):`, error);
    return { error: error.message };
  }
};

/** @deprecated */
export const setDirectDepositAccount = async (leadId: string, accountId: string) => {
  try {
    if (!leadId) {
      return {
        error: "Missing Lead Id"
      }
    }
    if (!accountId) {
      return {
        error: "Missing Account Id"
      }
    }

    const { data } = await client.mutate({
      mutation: SetDirectDepositAccount,
      variables: { leadId, accountId },
    });
    return {
      success: data.setDirectDepositAccount.success,
      directDepositAccountId: data.setDirectDepositAccount.directDepositAccountId,
    };
  } catch (error: any) {
    console.error("Unable to set direct deposit account:", error);
    return { error: error.message };
  }
}

export const setFlinksLoginId = async (leadId: string, loginId: string, accountId: string) => {
  try {
    if (!leadId) {
      return {
        error: "Missing Lead Id"
      }
    }
    if (!loginId) {
      return {
        error: "Missing Login Id"
      }
    }

    const { data } = await client.mutate({
      mutation: SetIncomeLogin,
      variables: { leadId, loginId, accountId },
    });
    return {
      success: data.setIncomeLogin.success,
      flinksLoginId: data.setIncomeLogin.loginId,
    };
  } catch (error: any) {
    console.error("Unable to set Flinks Login ID:", error);
    return { error: error.message };
  }
}
